/**
 * 全站状态标签的唯一入口。
 *
 * 配色走 tokens.css 的 --status-* 三件套，不用 AntD 的预设色名：
 * 预设色是 AntD 自己的一套语义，和我们的运行状态语义对不齐（比如 SKIPPED
 * 在 AntD 里没有对应物），而且深浅两套主题下的取值我们控制不了。
 *
 * 文案来自 utils/status.ts，那里是标签文字的单一来源（筛选下拉也读它）。
 */

import type { ReactNode } from 'react';
import { Tag, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  FieldTimeOutlined,
  LoadingOutlined,
  MinusCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import type { NodeStatus, RunStatus } from '@/types/api';
import { NODE_STATUS_META, RUN_STATUS_META } from '@/utils/status';

interface Tone {
  fg: string;
  bg: string;
  /**
   * 图标是**第二条**信息通道。颜色 + 图标形状 + 文案三重冗余，
   * 缺一条都不行：色觉障碍用户拿不到颜色，缩略视图里文案会被截断。
   */
  icon: ReactNode;
  /** SKIPPED 要整体压暗：它是「没走到」不是「出错了」 */
  opacity?: string;
  /** 虚线描边，进一步把 SKIPPED 与实心的失败态拉开 */
  dashed?: boolean;
}

const PENDING: Tone = {
  fg: 'var(--status-pending-fg)',
  bg: 'var(--status-pending-bg)',
  icon: <ClockCircleOutlined />,
};
const RUNNING: Tone = {
  fg: 'var(--status-running-fg)',
  bg: 'var(--status-running-bg)',
  icon: <LoadingOutlined />,
};
const SUCCESS: Tone = {
  fg: 'var(--status-success-fg)',
  bg: 'var(--status-success-bg)',
  icon: <CheckCircleOutlined />,
};
const FAILED: Tone = {
  fg: 'var(--status-failed-fg)',
  bg: 'var(--status-failed-bg)',
  icon: <CloseCircleOutlined />,
};
const TIMEOUT: Tone = {
  fg: 'var(--status-timeout-fg)',
  bg: 'var(--status-timeout-bg)',
  icon: <FieldTimeOutlined />,
};
const CANCELLED: Tone = {
  fg: 'var(--status-cancelled-fg)',
  bg: 'var(--status-cancelled-bg)',
  icon: <StopOutlined />,
};
/**
 * SKIPPED 是全站最容易被误读的状态：用户看到「不是成功」就以为出了事。
 * 所以它同时拿到三件不同的东西 —— 减号图标（不是叉）、灰化、虚线边框。
 */
const SKIPPED: Tone = {
  fg: 'var(--status-skipped-fg)',
  bg: 'var(--status-skipped-bg)',
  icon: <MinusCircleOutlined />,
  opacity: 'var(--status-skipped-opacity)',
  dashed: true,
};

const RUN_TONE: Record<RunStatus, Tone> = {
  QUEUED: PENDING,
  RUNNING,
  SUCCESS,
  FAILED,
  // 取消中是个过渡态：告警色表达「正在发生」，转圈图标表达「还没落定」
  CANCELLING: { ...TIMEOUT, icon: <LoadingOutlined /> },
  CANCELLED,
};

const NODE_TONE: Record<NodeStatus, Tone> = {
  PENDING,
  RUNNING,
  SUCCESS,
  FAILED,
  SKIPPED,
  TIMEOUT,
  CANCELLED,
};

const FALLBACK: Tone = {
  fg: 'var(--fg-secondary)',
  bg: 'var(--bg-sunken)',
  icon: <ClockCircleOutlined />,
};

interface RunProps {
  kind?: 'run';
  status: RunStatus;
  tooltip?: string;
}

interface NodeProps {
  kind: 'node';
  status: NodeStatus;
  tooltip?: string;
}

export function StatusTag(props: RunProps | NodeProps) {
  const isNode = props.kind === 'node';
  const meta = isNode
    ? NODE_STATUS_META[props.status as NodeStatus]
    : RUN_STATUS_META[props.status as RunStatus];
  const tone =
    (isNode
      ? NODE_TONE[props.status as NodeStatus]
      : RUN_TONE[props.status as RunStatus]) ?? FALLBACK;

  // 后端加了新状态时不该整行渲染崩掉，原样显示比抛错有用
  const label = meta?.label ?? String(props.status);

  const tag = (
    <Tag
      icon={tone.icon}
      style={{
        color: tone.fg,
        background: tone.bg,
        borderStyle: tone.dashed ? 'dashed' : 'solid',
        borderColor: tone.dashed ? 'currentColor' : 'transparent',
        opacity: tone.opacity,
        marginInlineEnd: 0,
        borderRadius: 'var(--radius-sm)',
        fontWeight: 500,
      }}
    >
      {label}
    </Tag>
  );

  return props.tooltip ? <Tooltip title={props.tooltip}>{tag}</Tooltip> : tag;
}
